// Spec s6: the field set is configuration, never code. Nothing in this app
// hardcodes a checklist anywhere, and the panel renders whatever is configured.

export type FieldType = 'string' | 'number' | 'date' | 'boolean' | 'enum';

export type ChecklistField = {
  /**
   * The state key extraction results are matched on. Assigned by the server and
   * NOT stable across a save - /checklists replaces a checklist's fields
   * wholesale rather than diffing them. That holds because a field id only has
   * to survive a single call, and a call does not span an edit of its own
   * checklist. Anything that needs to name a field across time needs a real key
   * first.
   */
  id: string;
  /** Shown in the panel. */
  label: string;
  /** Goes into the extraction prompt - this does the real work. */
  description: string;
  type: FieldType;
  /** Required when type is "enum". */
  enumValues?: string[];
};

export type ChecklistConfig = {
  id: string;
  name: string;
  /**
   * Set when the checklist is defined in this app's source rather than in the
   * database - currently only "pricing". The row exists so it can be selected
   * and recorded like any other; its fields come from `src/pricing/scope.ts`,
   * next to the scoring code whose columns their ids are.
   *
   * Such a checklist is not editable: renaming a field would leave the panel
   * working while the estimate stopped corresponding to anything.
   */
  builtinKey?: string | null;
  fields: ChecklistField[];
};

/** Everything the app holds about checklists. Mirrors one /checklists response. */
export type ConfigBlob = {
  selectedConfigId: string | null;
  configs: ChecklistConfig[];
};

/**
 * Where checklists lived before they moved to the cloud. Read once, lifted to
 * the server, then removed - see migrateLegacy in ConfigStore. Nothing writes
 * this key any more.
 */
export const LEGACY_STORAGE_KEY = 'checklist.configs.v1';
export const STORAGE_VERSION = 1;

/** The panel is designed for roughly 3-8 fields. Above that we warn, not block. */
export const FIELD_COUNT_WARN_THRESHOLD = 8;

/**
 * A client-side id for a field the user has just added to a draft. It survives
 * only until the draft is saved, at which point the server assigns the real
 * one. Checklist ids come from the server and are never generated here.
 */
export function newId(prefix: string): string {
  const random = Math.random().toString(36).slice(2, 6);
  return `${prefix}_${Date.now().toString(36)}${random}`;
}

export type FieldValidationError = { fieldId: string; message: string };

export function validateField(field: ChecklistField): FieldValidationError[] {
  const errors: FieldValidationError[] = [];
  if (!field.label.trim()) {
    errors.push({ fieldId: field.id, message: 'Label cannot be empty.' });
  }
  if (!field.description.trim()) {
    errors.push({
      fieldId: field.id,
      message: 'Description cannot be empty - it is what the extractor reads.',
    });
  }
  if (field.type === 'enum' && !(field.enumValues ?? []).some((v) => v.trim())) {
    errors.push({ fieldId: field.id, message: 'Enum fields need at least one allowed value.' });
  }
  return errors;
}

export function validateConfig(config: ChecklistConfig): FieldValidationError[] {
  const errors: FieldValidationError[] = [];
  if (!config.name.trim()) errors.push({ fieldId: '', message: 'Checklist name cannot be empty.' });
  if (config.fields.length === 0) {
    errors.push({ fieldId: '', message: 'A checklist needs at least one field.' });
  }
  for (const field of config.fields) errors.push(...validateField(field));
  return errors;
}
